import { Box } from '@mantine/core'
import { useDocumentVisibility, useIntersection } from '@mantine/hooks'
import type { ChatMessageWithSender } from '@markku/types'
import { useEffect, useRef, useState } from 'react'
import { MessageInput } from './MessageInput'
import { MessageList } from './MessageList'
import { ChatHooks } from './hooks'

interface ChatRoomProps {
  roomId: number
  currentUserId: number
  chatHooks: ChatHooks
  onDeleteMessage?: (messageId: number) => void
  onMessagesChange?: (messages: ChatMessageWithSender[]) => void
}

export function ChatRoom({
  roomId,
  currentUserId,
  chatHooks,
  onDeleteMessage,
  onMessagesChange,
}: ChatRoomProps) {
  const prevRoomIdRef = useRef<number>(roomId)
  const [lastMessageCreatedAt, setLastMessageCreatedAt] = useState<
    string | undefined
  >(undefined)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastMarkedMessageIdRef = useRef<number | null>(null)
  const [messages, setMessages] = useState<ChatMessageWithSender[]>([])
  const documentVisibility = useDocumentVisibility()

  // Track visibility of last message for read marking
  const { ref: lastMessageRef, entry: lastMessageEntry } = useIntersection({
    threshold: 0.1,
  })

  // Reset state synchronously when roomId changes (before hooks run)
  if (prevRoomIdRef.current !== roomId) {
    prevRoomIdRef.current = roomId
    setLastMessageCreatedAt(undefined)
    setMessages([])
    lastMarkedMessageIdRef.current = null
  }

  // Poll for new messages only when tab is visible
  const { data: messagesResult } = chatHooks.useListMessages({
    roomId,
    lastMessageCreatedAt,
    options: {
      enabled: !!roomId && documentVisibility === 'visible',
      refetchInterval: 2000,
    },
  })

  const sendMessage = chatHooks.useSendMessage()
  const markAsRead = chatHooks.useMarkAsRead()

  // Update message list when new messages arrive
  useEffect(() => {
    const newMessages = messagesResult?.data
    if (!newMessages?.length) return

    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id))
      const uniqueNewMessages = newMessages.filter(
        (m) => !existingIds.has(m.id),
      )
      const updated = [...prev, ...uniqueNewMessages]
      onMessagesChange?.(updated)
      return updated
    })

    // Update last message timestamp for incremental fetching
    const lastMessage = newMessages[newMessages.length - 1]
    setLastMessageCreatedAt(lastMessage.createdAt)

    // Auto-scroll to bottom when new messages arrive
    setTimeout(() => {
      messagesContainerRef.current?.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 500)
  }, [messagesResult, onMessagesChange])

  // Mark messages as read when last message is visible
  useEffect(() => {
    if (!lastMessageEntry?.isIntersecting) return

    // Find the last message not sent by current user
    const lastOtherMessage = messages.findLast(
      (m) => m.senderId !== currentUserId,
    )

    // Only mark if we have a message from someone else and haven't marked it yet
    if (
      lastOtherMessage &&
      lastMarkedMessageIdRef.current !== lastOtherMessage.id
    ) {
      lastMarkedMessageIdRef.current = lastOtherMessage.id
      markAsRead.mutate({
        roomId,
        lastReadMessageId: lastOtherMessage.id,
      })
    }
  }, [
    lastMessageEntry?.isIntersecting,
    messages,
    markAsRead,
    currentUserId,
    roomId,
  ])

  const handleSendMessage = async (
    content: string,
    attachmentIds?: number[],
  ) => {
    await sendMessage.mutateAsync({
      data: { roomId, content },
      ...(attachmentIds?.length && {
        attachments: attachmentIds.map((id) => ({
          id,
          action: 'add' as const,
        })),
      }),
    })
  }

  // Handle message deletion
  const handleDeleteMessage = (messageId: number) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, deletedAt: new Date().toISOString() }
          : msg,
      ),
    )
    onDeleteMessage?.(messageId)
  }

  return (
    <Box
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        ref={messagesContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
        p="md"
      >
        <MessageList
          messages={messages}
          currentUserId={currentUserId}
          onDeleteMessage={handleDeleteMessage}
          lastMessageRef={lastMessageRef}
        />
      </Box>

      <Box p="sm" pt="0">
        <MessageInput
          onSend={handleSendMessage}
          disabled={sendMessage.isPending}
          chatHooks={chatHooks}
        />
      </Box>
    </Box>
  )
}
